import { codespaceApi } from 'Services/ControlDataManagement/Features/Codespace/codespaceApi'
import { refundCodeApi } from 'Services/ControlDataManagement/Features/RefundCode/refundCodeApi'
import { zoneApi } from 'Services/ControlDataManagement/Features/Zone/zoneApi'
import { allowedApiClientApi } from 'Services/common/Features/AllowedApiClient/allowedApiClientApi'
import { authorityApi } from 'Services/common/Features/Authority/authorityApi'
import { catalogApi } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import { parameterApi } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { productApi } from 'Services/ProductManagement/Features/Product/productApi'
import { salesRuleApi } from 'Services/ProductManagement/Features/SalesRule/salesRuleApi'
import { tariffApi } from 'Services/ProductManagement/Features/Tariff/tariffApi'
import { vatDefinitionApi } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { zoneDefinitionApi } from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import { affiliateProductApi } from 'Services/SalesManagement/Features/AffiliateProduct/affiliateProductApi'
import { orderApi } from 'Services/SalesManagement/Features/Order/orderApi'
import { reportApi } from 'Services/SalesManagement/Features/Report/reportApi'
import {
  AFFILIATE_ORDER_REPORT_PREFIX,
  BULK_TICKET_SALE_REPORT_PREFIX,
  TICKET_SALE_REPORT_PREFIX,
  WEB_SHOP_ORDER_REPORT_PREFIX
} from 'Services/SalesManagement/Features/Report/reportSlice'
import { salesChannelApi } from 'Services/SalesManagement/Features/TicketSale/salesChannelApi'
import { ticketSaleApi } from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import { schoolApi } from 'Services/SchoolManagement/Features/School/schoolApi'
import { schoolCalendarApi } from 'Services/SchoolManagement/Features/SchoolCalendar/schoolCalendarApi'
import { deviceApi } from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import { salesPointApi } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import { serviceProviderApi } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import SnackbarUtils from 'Util/Snackbar'
import reduxWebsocket from '@giantmachines/redux-websocket'
import {
  type Action,
  configureStore,
  isRejectedWithValue,
  type Middleware,
  type ThunkAction
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { createSelectorHook, useDispatch } from 'react-redux'
import { t } from 'ttag'
import rootReducer, { type RootState } from './rootReducer'

const reduxWebsocketWebShopOrderReportMiddleware = reduxWebsocket({
  prefix: WEB_SHOP_ORDER_REPORT_PREFIX,
  deserializer: message => {
    if (typeof message === 'string') {
      try {
        return JSON.parse(message)
      } catch (e) {
        throw new Error('Websocket malformed json', { cause: e })
      }
    }

    return message
  }
})

const reduxWebsocketAffiliateOrderReportMiddleware = reduxWebsocket({
  prefix: AFFILIATE_ORDER_REPORT_PREFIX,
  deserializer: message => {
    if (typeof message === 'string') {
      try {
        return JSON.parse(message)
      } catch (e) {
        throw new Error('Websocket malformed json', { cause: e })
      }
    }

    return message
  }
})

const reduxWebsocketTicketSaleReportMiddleware = reduxWebsocket({
  prefix: TICKET_SALE_REPORT_PREFIX,
  deserializer: message => {
    if (typeof message === 'string') {
      try {
        return JSON.parse(message)
      } catch (e) {
        throw new Error('Websocket malformed json', { cause: e })
      }
    }

    return message
  }
})

const reduxWebsocketBulkTicketSaleReportMiddleware = reduxWebsocket({
  prefix: BULK_TICKET_SALE_REPORT_PREFIX,
  deserializer: message => {
    if (typeof message === 'string') {
      try {
        return JSON.parse(message)
      } catch (e) {
        throw new Error('Websocket malformed json', { cause: e })
      }
    }

    return message
  }
})

export const rtkQueryErrorLogger: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    if (action.payload.originalStatus >= 500) {
      SnackbarUtils.error(t`Unexpected error occurred. Please try again later.`)
      Sentry.addBreadcrumb({
        type: action.type,
        message: action.payload,
        data: { payload: action.meta.arg }
      })
      Sentry.captureException(`API request exception: ${action.error.message}`)
    } else if (action.payload.originalStatus === 404) {
      SnackbarUtils.error(t`Resource not found.`)
    }
  }

  return next(action)
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          // Ignore all Redux Websocket actions
          `${WEB_SHOP_ORDER_REPORT_PREFIX}::OPEN`,
          `${WEB_SHOP_ORDER_REPORT_PREFIX}::CLOSE`,
          `${WEB_SHOP_ORDER_REPORT_PREFIX}::CLOSED`,
          `${WEB_SHOP_ORDER_REPORT_PREFIX}::MESSAGE`,
          `${WEB_SHOP_ORDER_REPORT_PREFIX}::DISCONNECT`,
          `${WEB_SHOP_ORDER_REPORT_PREFIX}::ERROR`,
          `${AFFILIATE_ORDER_REPORT_PREFIX}::OPEN`,
          `${AFFILIATE_ORDER_REPORT_PREFIX}::CLOSE`,
          `${AFFILIATE_ORDER_REPORT_PREFIX}::CLOSED`,
          `${AFFILIATE_ORDER_REPORT_PREFIX}::MESSAGE`,
          `${AFFILIATE_ORDER_REPORT_PREFIX}::DISCONNECT`,
          `${AFFILIATE_ORDER_REPORT_PREFIX}::ERROR`,
          `${TICKET_SALE_REPORT_PREFIX}::OPEN`,
          `${TICKET_SALE_REPORT_PREFIX}::CLOSE`,
          `${TICKET_SALE_REPORT_PREFIX}::CLOSED`,
          `${TICKET_SALE_REPORT_PREFIX}::MESSAGE`,
          `${TICKET_SALE_REPORT_PREFIX}::DISCONNECT`,
          `${TICKET_SALE_REPORT_PREFIX}::ERROR`,
          `${BULK_TICKET_SALE_REPORT_PREFIX}::OPEN`,
          `${BULK_TICKET_SALE_REPORT_PREFIX}::CLOSE`,
          `${BULK_TICKET_SALE_REPORT_PREFIX}::CLOSED`,
          `${BULK_TICKET_SALE_REPORT_PREFIX}::MESSAGE`,
          `${BULK_TICKET_SALE_REPORT_PREFIX}::DISCONNECT`,
          `${BULK_TICKET_SALE_REPORT_PREFIX}::ERROR`,
          // Ignore file download actions
          `webShopOrder/fetchWebShopOrderReceipt/fulfilled`
        ],
        ignoredActionPaths: ['meta.arg', 'meta.timestamp', 'meta.baseQueryMeta']
      }
    }).concat([
      authorityApi.middleware,
      allowedApiClientApi.middleware,
      salesChannelApi.middleware,
      ticketSaleApi.middleware,
      reportApi.middleware,
      orderApi.middleware,
      affiliateProductApi.middleware,
      vatDefinitionApi.middleware,
      parameterApi.middleware,
      zoneDefinitionApi.middleware,
      salesRuleApi.middleware,
      refundCodeApi.middleware,
      codespaceApi.middleware,
      zoneApi.middleware,
      catalogApi.middleware,
      productApi.middleware,
      tariffApi.middleware,
      salesPointApi.middleware,
      serviceProviderApi.middleware,
      deviceApi.middleware,
      schoolApi.middleware,
      schoolCalendarApi.middleware,
      reduxWebsocketWebShopOrderReportMiddleware,
      reduxWebsocketAffiliateOrderReportMiddleware,
      reduxWebsocketTicketSaleReportMiddleware,
      reduxWebsocketBulkTicketSaleReportMiddleware,
      rtkQueryErrorLogger
    ])
})

if (import.meta.env.DEV && import.meta.hot) {
  import.meta.hot.accept('./rootReducer', () => {
    // eslint-disable-next-line
    const { default: newRootReducer } = require('./rootReducer')
    store.replaceReducer(newRootReducer)
  })
}

export type { RootState } // re-export
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>

export type LoadingStates = 'idle' | 'pending' | 'fulfilled' | 'rejected'
export interface Translation {
  description: string
  locale: string
  name: string
  short_name: string
  additional_name: string
  value: string
}

export interface ParameterTranslation {
  locale: string
  name: string
}

export const useAppDispatch = () => useDispatch<typeof store.dispatch>() // Export a hook that can be reused to resolve types

export const useTypedSelector = createSelectorHook<RootState>()

export default useTypedSelector
